import VenueCard from "@/components/VenueCard/VenueCard";
import VenueCardSkeleton from "@/components/VenueCard/VenueCardSkeleton";
import { useAuth, useFilters, useI18N, useSavedPlaces } from "common/hooks";
import { useBreakpointForVenueList } from "common/config";
import { Venue } from "common/types";
import { FilteredVenue } from "common/apis/search";
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert";
import { Info, X } from "lucide-react";
import NoSearchResults from "@/assets/images/noresults.svg";
import NoSavcedPlacesResults from "@/assets/images/EmptyList.svg";
import NoSearchResultsFilter from "@/assets/images/noresultsfilter.svg";
import { useAtom } from "jotai";
import {
  filtersOpenAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  venueDetailsOpenedAtom,
  viewModeAtom,
  searchOverlayOpenAtom,
} from "@/state/atoms";
import { useSearchPageParams } from "common/hooks/business/home/useSearchPageParams";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { enableGoogleFallback } from "common/config";
import { StandardButton } from "@/components/common/StandardButton";
import Modal from "react-responsive-modal";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";
import AnalyticsService from "@/services/analytics";
import { useNavigate } from "@tanstack/react-router";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import { useScrollLock } from "@/hooks/useScrollLock";
import { Combobox, ComboboxOption } from "@/shadcn/components/ui/combobox";

type VenueListProps = {
  /** Array of venues to display */
  locations: Venue[];
  /** Loading state for venues */
  isLoading: boolean;
  /** Callback when a venue is clicked */
  onVenueClick: (location: Venue) => void;
  /** Callback when get alert button is clicked */
  onGetAlert?: (venueId: string | number) => void;
  /** Set of venue IDs with notifications enabled */
  notificationVenueIds?: Set<number>;
  /** Authentication state passed from parent */
  isLoggedIn?: boolean;
  /** Handler to show auth modal */
  onShowAuthModal?: () => void;
  /** Total count of results from API */
  totalCount?: number;
  /** Filtered venues that were excluded from results */
  filteredVenues?: FilteredVenue[];
};

function VenueList(props: VenueListProps) {
  const {
    locations,
    isLoading,
    onVenueClick,
    onGetAlert,
    notificationVenueIds,
    isLoggedIn = false,
    onShowAuthModal,
    totalCount,
    filteredVenues,
  } = props;

  const { filtersState } = useFilters();
  const { user, isMapper } = useAuth();
  const i18n = useI18N();
  const { searchPageParams, setSearchPageParams } = useSearchPageParams();
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  useScrollLock(openSavedPlacesModal);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);
  const { refetch, data } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];
  const [listNameCreated, setListNameCreated] = useState("");
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [filteredVenuesDismissed, setFilteredVenuesDismissed] = useState(false);
  const [viewMode, setViewMode] = useAtom(viewModeAtom);
  const [mappingStatusFilter, setMappingStatusFilter] = useState<"all" | "mapped" | "unmapped">("all");

  // Reset dismissed state when filtered venues change
  useEffect(() => {
    setFilteredVenuesDismissed(false);
  }, [filteredVenues]);

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  // Sync mapping status filter with searchPageParams (for navigation from Home page)
  useEffect(() => {
    if (searchPageParams.mapping_status === "mapped") {
      setMappingStatusFilter("mapped");
    } else if (searchPageParams.mapping_status === "unmapped") {
      setMappingStatusFilter("unmapped");
    } else {
      setMappingStatusFilter("all");
    }
  }, [searchPageParams.mapping_status]);

  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode,
    );

    AnalyticsService.trackGAEventHandler("mySavedPlacesExitSave");
    AnalyticsService.trackMx("mySavedPlacesExitSave", {});

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  const handleNotificationClick = useCallback(
    (venue: Venue) => {
      AnalyticsService.trackGAEventHandler("venueListGetAlertClicked");
      AnalyticsService.trackMx("venueListGetAlertClicked", {
        VenueId: venue.id,
        VenueName: venue.name,
        VenueType: venue.businessType,
      });
      onGetAlert?.(venue.id);
    },
    [onGetAlert],
  );

  const handleViewModeChange = useCallback((value: string) => {
    if (value && (value === "mapper" || value === "user")) {
      setViewMode(value as "mapper" | "user");
      
      // Update search params to trigger API refetch
      setSearchPageParams((prev) => ({
        ...prev,
        tagging_mode: value === "mapper" ? "on" : "off",
        section_filter: undefined, // Clear section filter when changing modes
        unixTimestamp: Math.floor(Date.now() / 1000), // Force refetch        
      }));
      
      // Track analytics
      AnalyticsService.trackGAEventHandler("venueListViewModeChanged");
      AnalyticsService.trackMx("venueListViewModeChanged", {
        viewMode: value,
      });
    }
  }, [setSearchPageParams]);

  // View mode options for the combobox
  const viewModeOptions: ComboboxOption[] = useMemo(() => [
    { 
      value: "mapper", 
      label: i18n.get("mapperView") || "Mapper View" 
    },
    { 
      value: "user", 
      label: i18n.get("userView") || "User View" 
    },
  ], [i18n]);

  // Mapping status filter options for the combobox
  const mappingStatusOptions: ComboboxOption[] = useMemo(() => [
    { 
      value: "all", 
      label: i18n.get("allVenues") || "All venues" 
    },
    { 
      value: "mapped", 
      label: i18n.get("venuesWithData") || "Venues with data" 
    },
    { 
      value: "unmapped", 
      label: i18n.get("venuesWithoutData") || "Venues without data" 
    },
  ], [i18n]);

  // Handle mapping status filter change
  const handleMappingStatusChange = useCallback((value: string) => {
    if (value && (value === "all" || value === "mapped" || value === "unmapped")) {
      AnalyticsService.trackGAEventHandler("searchMappingStatusFilterChanged");
      AnalyticsService.trackMx("searchMappingStatusFilterChanged", {
        FilterValue: value,
      });
      setMappingStatusFilter(value as "all" | "mapped" | "unmapped");
      
      // Update search params to trigger API refetch
      setSearchPageParams((prev) => ({
        ...prev,
        mapping_status: value === "all" ? undefined : value,
        section_filter: undefined,
        tagging_mode: "off",
        unixTimestamp: Math.floor(Date.now() / 1000), // Force refetch
      }));
      
      // Track analytics
      AnalyticsService.trackGAEventHandler("venueListMappingStatusChanged");
      AnalyticsService.trackMx("venueListMappingStatusChanged", {
        mappingStatus: value,
      });
    }
  }, [setSearchPageParams]);


  const gridClassesOuter =
    useBreakpointForVenueList === "Yes"
      ? "grid pb-10 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-2 m-2 md:gap-3"
      : "grid md:grid-cols-2 gap-6 md:gap-6";

  const gridClassesInner =
    useBreakpointForVenueList === "Yes"
      ? "grid pb-10 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-3 ml-2 mr-4 md:gap-4"
      : "grid pb-10 md:grid-cols-2 gap-3 ml-2 mr-4 md:gap-4";

  return isLoading ? (
    <div className={gridClassesOuter}>
      {Array.from({ length: 4 }).map((_, index) => (
        <VenueCardSkeleton key={index} />
      ))}
    </div>
  ) : (
    <>
      {/* Divider */}
      <div className="shrink-0 h-px border border-border-token-neutral-minimal ml-2 mr-4" aria-hidden="true" />

      {/* Results Header — always visible when not loading */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-start gap-3 px-2 md:px-4 py-3">
        {/* Results count */}
        <h2
          className="label-m-medium text-fg-neutral-subtle"
          aria-live="polite"
          aria-atomic="true"
        >
          {`${locations.length} ${i18n.get("results") || "Results"}${totalCount && totalCount > 0 && totalCount > locations.length ? ` (${totalCount} Total)` : ""}`}
        </h2>

        {/* Mapper/User View Combobox - Only show for mappers */}
        {isMapper && (
          <Combobox
            options={viewModeOptions}
            value={viewMode}
            onValueChange={handleViewModeChange}
            className="w-full md:w-[160px]"
          />
        )}

        {/* Mapping Status Filter - Show for standard users OR mappers in User View */}
        {(!isMapper || viewMode === "user") && (
          <Combobox
            options={mappingStatusOptions}
            value={mappingStatusFilter}
            onValueChange={handleMappingStatusChange}
            className="w-full md:w-[200px]"
          />
        )}
      </div>

      {/* Body: empty state OR venue list */}
      {locations.length === 0 ? (
        filtersState.isFiltersApplied && !savedPlaceIdToView ? (
          <VenueListNoFilterResults />
        ) : savedPlaceIdToView ? (
          <VenueListNoSavedPlacesResults />
        ) : (
          <VenueListNoSearchResults filteredVenues={filteredVenues} />
        )
      ) : (
        <>
          {/* Filtered Venues Message */}
          {filteredVenues && filteredVenues.length > 0 && !filteredVenuesDismissed && (() => {
            // Collect first type from each filtered venue
            const firstTypes = filteredVenues
              .filter(venue => venue.reason === 'business_type_not_allowed' || venue.reason === 'invalid_business_type')
              .map(venue => venue.types[0])
              .filter(type => type);

            const typesString = firstTypes.join(', ');
            const venueCount = filteredVenues.length;
            const venueText = venueCount === 1 ? 'venue' : 'venues';

            return (
              <div className="px-2 md:px-4">
                <Alert className="my-2 bg-container-brand-minimal relative">
                  <Info className="h-4 w-4" style={{ stroke: '#352FB8' }} />
                  <AlertDescription className="label-m-medium text-fg-neutral-subtle">
                    {venueCount} {venueText} hidden due to unsupported venue type{typesString && <span> - {typesString}</span>}
                  </AlertDescription>
                  <button
                    onClick={() => {
                      AnalyticsService.trackGAEventHandler("searchFilteredVenuesAlertDismissed");
                      AnalyticsService.trackMx("searchFilteredVenuesAlertDismissed", {
                        FilteredCount: venueCount,
                      });
                      setFilteredVenuesDismissed(true);
                    }}
                    className="absolute right-4 top-4 text-fg-neutral-subtle hover:text-fg-neutral-strong transition-colors"
                    aria-label="Close message"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Alert>
              </div>
            );
          })()}

          <ul
            className={`${gridClassesInner} overflow-visible`}
            aria-label="Search results"
          >
            {locations.map((location, index) => (
              <li key={`${location.id}-${index}`}>
                <VenueCard
                  location={location}
                  onOpen={(location) => {
                    AnalyticsService.trackGAEventHandler("searchVenueCardClicked");
                    AnalyticsService.trackMx("searchVenueCardClicked", {
                      VenueId: location.id,
                      VenueName: location.name,
                      VenueType: location.businessType,
                    });
                    onVenueClick(location);
                  }}
                  onSaveVenue={handleSaveVenue}
                  onNotificationClick={handleNotificationClick}
                  hasNotification={notificationVenueIds?.has(location.business?.id)}
                  savedPlaces={savedPlaces}
                  truncateText={false}
                  index={index}
                  totalCount={locations.length}
                  showDescription={true}
                  showAddress={false}
                  showOpeningHours={true}
                  showMetaInfo={true}
                  showVariantIndicators={true}
                  enableArrowNavigation={true}
                  showActionButtons={true}
                  isLoggedIn={isLoggedIn}
                  onShowAuthModal={onShowAuthModal}
                />
              </li>
            ))}

            {savedPlaceIdToView && locations.length <= 4 && (
              <DiscoverMoreVenuesCard />
            )}

            {openSavedPlacesModal && (
              <Modal
                focusTrapped
                center
                blockScroll={true}
                animationDuration={0}
                open={openSavedPlacesModal}
                onClose={() => handleSavedPlacesModalClose()}
                classNames={{
                  modal:
                    "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
                  closeButton: "hidden",
                }}
              >
                {savePlaceListMode === "create" && (
                  <CreateList
                    onClose={() => handleSavedPlacesModalClose()}
                    businessId={locationForSavedPlaces}
                    venueDetails={location}
                    onCreateList={(listNameCreated) =>
                      handleCreateList(listNameCreated)
                    }
                  />
                )}

                {savePlaceListMode === "list" && (
                  <AddRemoveVenueToList
                    onClickCreateNew={() => handleCreateNewClick()}
                    onClose={() => handleSavedPlacesModalClose()}
                    businessId={locationForSavedPlaces}
                    venueDetails={location}
                    listNameCreated={listNameCreated}
                  />
                )}
              </Modal>
            )}
          </ul>
        </>
      )}
    </>
  );
}

export default VenueList;

function DiscoverMoreVenuesCard() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const navigate = useNavigate({ from: "/" });

  const { setSelectedVenue } = useSelectedVenue();
  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);

  const clearsetSelectedVenue = () => {
    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

    navigate({ to: "/" });
  };

  return (
    <div className="w-full h-[396px] px-2 py-6 bg-container-brand-minimal rounded-xl flex-col justify-center items-center gap-4 inline-flex">
      {/* Icon Container */}
      <div className="flex justify-center items-center mb-2">
        <img
          src={
            isDark
              ? "/images/MagnifyingGlass_discover_dark.svg"
              : "/images/MagnifyingGlass_discover.svg"
          }
          alt=""
          aria-hidden="true"
        />
      </div>

      <div className="flex-col justify-start items-start gap-1 flex">
        <div className="self-stretch h-5 text-center text-fg-neutral-strong title-s-semibold">
          {i18n.get("discoverMoreVenues")}
          <br />
        </div>
      </div>

      <div className="w-full text-center text-fg-neutral-strong label-m-medium max-w-[80%]">
        {i18n.get("discoverMoreVenuesMessage")}
      </div>

      <div className="flex flex-col justify-center items-center w-full py-3 max-w-[80%]">
        <WideButton className="grow" onClick={() => clearsetSelectedVenue()}>
          {i18n.get("explore")}
        </WideButton>
      </div>
    </div>
  );
}

function VenueListNoSavedPlacesResults() {
  const i18n = useI18N();
  const navigate = useNavigate({ from: "/" });

  const { setSelectedVenue } = useSelectedVenue();
  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);

  const noSavedPlacesRef = useRef(null); // Reference for the paragraph

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noSavedPlacesRef.current) {
      noSavedPlacesRef.current.focus();
    }
  }, []);

  const clearsetSelectedVenue = () => {
    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);
    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");
    navigate({ to: "/" });
  };

  return (
    <div
      className="flex flex-col items-center justify-center flex-1"
      aria-label="No venue found"
    >
      <img
        src={NoSavcedPlacesResults}
        alt={i18n.get("savedPlacesListEmpty")}
        className="md:max-w-[50%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noSavedPlacesRef} // Attach the ref here
        className="mt-3 py-2 md:py-0 mx-auto text-left md:text-center title-s-semibold text-fg-neutral-strong"
      >
        {i18n.get("savedPlacesListEmpty")}
      </p>
      <p className="label-m-medium py-2 md:py-0 md:w-[444px] mt-2 mx-auto text-left md:text-center text-natural-700">
        {i18n.get("savedPlacesListEmptyMessage")}
      </p>

      <div className="flex flex-col justify-center items-center w-full py-6 md:py-3 md:max-w-[40%]">
        <WideButton className="grow" onClick={() => clearsetSelectedVenue()}>
          {i18n.get("exploreNearby")}
        </WideButton>
      </div>
    </div>
  );
}

function VenueListNoSearchResults({ filteredVenues }: { filteredVenues?: FilteredVenue[] }) {
  const i18n = useI18N();
  const { setSearchPageParams } = useSearchPageParams();
  const noResultsRef = useRef(null);

  const [, setSearchOverlayOpen] = useAtom(searchOverlayOpenAtom);
  const [filteredVenuesDismissed, setFilteredVenuesDismissed] = useState(false);

  // Reset dismissed state when filtered venues change
  useEffect(() => {
    setFilteredVenuesDismissed(false);
  }, [filteredVenues]);

  const handleUncoverMore = () => {
    // // Previous behavior: Set includeGoogleFallback to true and trigger a search
    // setSearchPageParams((prev) => ({
    //   ...prev,
    //   includeGoogleFallback: "true",
    // }));

    // Open the search overlay and focus on the search input
    setSearchOverlayOpen(true);
  };

  const handleViewAvailablePlaces = () => {
    AnalyticsService.trackGAEventHandler("searchExpandRadiusClicked");
    AnalyticsService.trackMx("searchExpandRadiusClicked", {});
    // Trigger an expand_radius search and let the existing auto-zoom logic
    // (based on search_distance_used / search_original_distance from API)
    // handle the map zoom level automatically
    setSearchPageParams((prev) => ({
      ...prev,
      search_type: "expand_radius",
      autoZoom: "yes",
      unixTimestamp: Math.floor(Date.now() / 1000),
    }));
  };

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 mt-5"
      aria-label="No venue found"
      tabIndex={-1}
    >
      {/* Filtered Venues Message - At the top */}
      {filteredVenues && filteredVenues.length > 0 && !filteredVenuesDismissed && (() => {
        // Collect first type from each filtered venue
        const firstTypes = filteredVenues
          .filter(venue => venue.reason === 'business_type_not_allowed' || venue.reason === 'invalid_business_type')
          .map(venue => venue.types[0])
          .filter(type => type);
        
        const typesString = firstTypes.join(', ');
        const venueCount = filteredVenues.length;
        const venueText = venueCount === 1 ? 'venue' : 'venues';
        
        return (
          <div className="w-full max-w-md mb-4 px-4">
            <Alert className="my-2 bg-container-brand-minimal relative">
              <Info className="h-4 w-4" style={{ stroke: '#352FB8' }} />
              <AlertDescription className="label-m-medium text-fg-neutral-subtle">
                {venueCount} {venueText} hidden due to unsupported venue type{typesString && <span> - {typesString}</span>}
              </AlertDescription>
              <button
                onClick={() => {
                  AnalyticsService.trackGAEventHandler("searchFilteredVenuesAlertDismissed");
                  AnalyticsService.trackMx("searchFilteredVenuesAlertDismissed", {
                    FilteredCount: venueCount,
                  });
                  setFilteredVenuesDismissed(true);
                }}
                className="absolute right-4 top-4 text-fg-neutral-subtle hover:text-fg-neutral-strong transition-colors"
                aria-label="Close message"
              >
                <X className="h-4 w-4" />
              </button>
            </Alert>
          </div>
        );
      })()}

      <img
        src={NoSearchResults}
        alt={i18n.get("noResultsFound")}
        className="md:max-w-[50%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noResultsRef}
        className="mt-3 py-2 md:py-0 mx-auto text-left md:text-center title-s-semibold text-fg-neutral-strong"
      >
        {enableGoogleFallback
          ? i18n.get("weHaventReachedYourAreaYet")
          : i18n.get("noResultsFound")}
      </p>
      <p className="label-m-medium py-2 md:py-0 md:w-[444px] mt-2 mx-auto text-left md:text-center text-natural-700">
        {enableGoogleFallback
          ? i18n.get("exploreOrUncoverMore")
          : i18n.get("bitLostMessageSearch")}
      </p>

      {enableGoogleFallback && (
        <div className="flex flex-col w-full max-w-xs gap-3 mt-6">
          <StandardButton
            onClick={handleViewAvailablePlaces}
            variant="primary"
            fullWidth
          >
            {i18n.get("viewAvailablePlaces")} 
          </StandardButton>

          <StandardButton
            onClick={handleUncoverMore}
            variant="secondary"
            fullWidth
          >
            {i18n.get("uncoverMore")}
          </StandardButton>
        </div>
      )}
    </div>
  );
}

function VenueListNoFilterResults() {
  const i18n = useI18N();
  const [, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);

  const noFilterResultsRef = useRef(null); // Reference for the paragraph

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noFilterResultsRef.current) {
      noFilterResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 mt-5"
      aria-label="No venue found"
    >
      <img
        src={NoSearchResultsFilter}
        alt={i18n.get("noMatches")}
        className="md:max-w-[50%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noFilterResultsRef} // Attach the ref here
        className="mt-3 py-2 md:py-0 mx-auto text-left md:text-center title-s-semibold text-fg-neutral-strong"
      >
        {i18n.get("noMatches")}
      </p>
      <p className="label-m-medium py-2 md:py-0 md:w-[444px] mt-2 mx-auto text-left md:text-center text-natural-700">
        {i18n.get("tryUpdateFilters")}
      </p>

      <div className="flex flex-col justify-center items-center w-full py-6 md:py-3 md:max-w-[40%]">
        <WideButton
          className="grow"
          onClick={() => {
            setIsFilterSheetOpen(true);
          }}
        >
          {i18n.get("updateFilters")}
        </WideButton>
      </div>
    </div>
  );
}
